import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ImportedEvent } from "@myams/shared";
import {
  useAdminEvents, useCreateAdminEvent, useDeleteAdminEvent, useUpdateAdminEvent,
} from "../../lib/queries";

const CATEGORY_OPTIONS = [
  "", "music", "food", "art", "market", "cinema", "outdoor", "cafe", "queer", "sports", "hidden",
];

interface FormState {
  title: string;
  description: string;
  category: string;
  neighborhood: string;
  url: string;
  imageUrl: string;
  address: string;
  lat: string;
  lng: string;
  startsAt: string;  // ISO local datetime ("yyyy-MM-ddTHH:mm")
  endsAt: string;
  hidden: boolean;
}

const EMPTY: FormState = {
  title: "", description: "", category: "", neighborhood: "",
  url: "", imageUrl: "", address: "", lat: "", lng: "",
  startsAt: "", endsAt: "", hidden: false,
};

/** unix-seconds → "yyyy-MM-ddTHH:mm" in Europe/Amsterdam for <input type="datetime-local">. */
function tsToLocalInput(ts: number | null | undefined): string {
  if (ts == null) return "";
  // datetime-local has no tz; we display Amsterdam local. Compute parts via Intl.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Amsterdam",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date(ts * 1000));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

/** "yyyy-MM-ddTHH:mm" → unix seconds, treating the input as Europe/Amsterdam.
 *  Approximation: assumes the offset is +01:00 (winter) or +02:00 (summer DST).
 *  Wrong-by-an-hour at the exact moment of DST switch — acceptable for admin tooling. */
function localInputToTs(s: string): number | null {
  if (!s) return null;
  // Parse the literal then ask the runtime what the Amsterdam offset is for
  // *that* instant by round-tripping through Intl.
  const literal = new Date(`${s}:00Z`); // treat as UTC first
  if (Number.isNaN(literal.getTime())) return null;
  // Find the Amsterdam offset at this instant.
  const offsetMin = amsterdamOffsetMinutes(literal);
  return Math.floor((literal.getTime() - offsetMin * 60_000) / 1000);
}

function amsterdamOffsetMinutes(at: Date): number {
  // Returns the offset of Europe/Amsterdam from UTC at the given instant,
  // in minutes (positive east). CET = +60, CEST = +120.
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Amsterdam",
    timeZoneName: "shortOffset",
  });
  const parts = dtf.formatToParts(at);
  const off = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT";
  const m = /GMT([+-])(\d{1,2})(?::?(\d{2}))?/.exec(off);
  if (!m) return 60; // safe default
  const sign = m[1] === "-" ? -1 : 1;
  const h = Number(m[2] ?? "0");
  const min = Number(m[3] ?? "0");
  return sign * (h * 60 + min);
}

export function AdminEventEdit() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  // We don't have a single-event admin fetch endpoint, but the events
  // list is already in cache from /admin/events, so we read from there.
  // Cheap and avoids round-tripping the server for the edit page.
  const { data: allEvents = [], isPending: listLoading } = useAdminEvents();
  const event = id ? allEvents.find((e) => e.id === id) : undefined;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // React re-uses the component when navigating between
  // /admin/events/A/edit → /admin/events/B/edit, so we have to reset
  // `hydrated` explicitly so the prefill effect runs again for B.
  useEffect(() => { setHydrated(false); }, [id]);

  // Prefill once the event is found in cache.
  useEffect(() => {
    if (isNew) { setForm(EMPTY); setHydrated(true); return; }
    if (event && !hydrated) {
      setForm({
        title: event.title ?? "",
        description: event.description ?? "",
        category: event.category ?? "",
        neighborhood: event.neighborhood ?? "",
        url: event.url ?? "",
        imageUrl: event.imageUrl ?? "",
        address: event.address ?? "",
        lat: event.lat == null ? "" : String(event.lat),
        lng: event.lng == null ? "" : String(event.lng),
        startsAt: tsToLocalInput(event.startsAt),
        endsAt: tsToLocalInput(event.endsAt),
        hidden: !!event.hidden,
      });
      setHydrated(true);
    }
  }, [isNew, event, hydrated]);

  const create = useCreateAdminEvent();
  const update = useUpdateAdminEvent();
  const del = useDeleteAdminEvent();

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.title.trim()) { setError("Title is required."); return; }
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      category: form.category || null,
      neighborhood: form.neighborhood.trim() || null,
      url: form.url.trim() || undefined,   // undefined → server keeps existing
      imageUrl: form.imageUrl.trim() || null,
      address: form.address.trim() || null,
      lat: form.lat ? Number(form.lat) : null,
      lng: form.lng ? Number(form.lng) : null,
      startsAt: localInputToTs(form.startsAt),
      endsAt: localInputToTs(form.endsAt),
      hidden: form.hidden,
    };
    try {
      if (isNew) {
        await create.mutateAsync(payload);
      } else {
        await update.mutateAsync({ id: id!, ...payload });
      }
      navigate("/admin/events");
    } catch (err: any) {
      setError(String(err?.message ?? err).slice(0, 200));
    }
  };

  const onDelete = async () => {
    if (!id) return;
    if (!confirm(`Delete "${event?.title}"? This cannot be undone.`)) return;
    try {
      await del.mutateAsync(id);
      navigate("/admin/events");
    } catch (err: any) {
      setError(String(err?.message ?? err).slice(0, 200));
    }
  };

  if (!isNew && listLoading && !hydrated) {
    return (
      <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", textAlign: "center", padding: 32 }}>
        loading…
      </div>
    );
  }
  if (!isNew && !event && hydrated) {
    return (
      <div style={{ padding: 24 }}>
        <div className="ma-display" style={{ fontSize: 22, marginBottom: 8 }}>Event not found</div>
        <button onClick={() => navigate("/admin/events")} style={primaryBtn}>back to list</button>
      </div>
    );
  }

  const submitting = create.isPending || update.isPending;

  return (
    <form onSubmit={onSave} style={{ padding: "16px 14px 32px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
        <div className="ma-display" style={{ fontSize: 22 }}>
          {isNew ? "New event" : `Edit · ${event?.source}`}
        </div>
        <button type="button" onClick={() => navigate("/admin/events")} style={secondaryBtn}>cancel</button>
      </div>

      {/* Two-column grid on desktop, single column on mobile.
          Title + description span the full width; dates / location etc.
          pair up on wider screens. */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: "0 14px",
      }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="Title *">
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required style={inputStyle} />
          </Field>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="Description">
            <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={4} style={{ ...inputStyle, fontFamily: "var(--ma-body)", resize: "vertical" }} />
          </Field>
        </div>

        <Field label="Starts (Europe/Amsterdam)">
          <input type="datetime-local" value={form.startsAt} onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))} style={inputStyle} />
        </Field>
        <Field label="Ends (optional)">
          <input type="datetime-local" value={form.endsAt} onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))} style={inputStyle} />
        </Field>

        <Field label="Category">
          <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} style={inputStyle}>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>{c || "(none)"}</option>
            ))}
          </select>
        </Field>
        <Field label="Neighborhood">
          <input value={form.neighborhood} onChange={(e) => setForm((f) => ({ ...f, neighborhood: e.target.value }))} style={inputStyle} />
        </Field>

        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="URL (ticketing / source page)">
            <input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://…" style={inputStyle} />
          </Field>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="Image URL">
            <input value={form.imageUrl} onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))} placeholder="https://…" style={inputStyle} />
          </Field>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <Field label="Address">
            <input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} style={inputStyle} />
          </Field>
        </div>

        <Field label="Latitude">
          <input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))} style={inputStyle} />
        </Field>
        <Field label="Longitude">
          <input type="number" step="0.0001" value={form.lng} onChange={(e) => setForm((f) => ({ ...f, lng: e.target.value }))} style={inputStyle} />
        </Field>

        <div style={{ gridColumn: "1 / -1", marginTop: 6 }}>
          <label style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "12px 14px",
            background: form.hidden ? "var(--ma-paper)" : "var(--ma-cream)",
            border: form.hidden ? "var(--ma-border) solid var(--ma-brick)" : "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            cursor: "pointer",
          }}>
            <input
              type="checkbox"
              checked={form.hidden}
              onChange={(e) => setForm((f) => ({ ...f, hidden: e.target.checked }))}
              style={{ accentColor: "var(--ma-brick)", width: 18, height: 18 }}
            />
            <div>
              <div className="ma-mono" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: form.hidden ? "var(--ma-brick)" : "var(--ma-ink)", fontWeight: 600 }}>
                {form.hidden ? "hidden from consumers" : "visible to consumers"}
              </div>
              <div style={{ fontFamily: "var(--ma-body)", fontSize: 12, color: "var(--ma-ink-soft)", marginTop: 2 }}>
                Hidden events are kept in the DB so the scraper doesn't re-import them next tick, but don't appear in search / now / map.
              </div>
            </div>
          </label>
        </div>
      </div>

      {error && (
        <div className="ma-mono" style={{
          fontSize: 11, color: "var(--ma-brick)", letterSpacing: "0.08em",
          padding: "10px 12px", marginTop: 10,
          background: "var(--ma-cream)", border: "var(--ma-border) solid var(--ma-brick)",
          borderRadius: "var(--ma-card-radius)",
        }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
        <button type="submit" disabled={submitting} style={{ ...primaryBtn, flex: 1, padding: "14px 0", fontSize: 12 }}>
          {submitting ? "saving…" : isNew ? "create event" : "save changes"}
        </button>
        {!isNew && (
          <button type="button" onClick={onDelete} disabled={del.isPending} style={{
            ...secondaryBtn, padding: "14px 16px", borderColor: "var(--ma-brick)", color: "var(--ma-brick)",
          }}>
            {del.isPending ? "deleting…" : "delete"}
          </button>
        )}
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 12, flex: 1 }}>
      <div className="ma-mono" style={{
        fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase",
        color: "var(--ma-ink-soft)", marginBottom: 4,
      }}>
        {label}
      </div>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "10px 12px",
  background: "var(--ma-cream)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 16,
  color: "var(--ma-ink)", boxSizing: "border-box",
};
const primaryBtn: React.CSSProperties = {
  padding: "10px 14px",
  background: "var(--ma-ink)", color: "var(--ma-paper)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
  textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
};
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  background: "transparent", color: "var(--ma-ink)",
};
